import { useCallback, useEffect, useRef, useState } from "react";

import {
  BobbinMobileError,
  type BobbinMobileClient,
} from "./client";
import {
  bobbinNodeTitle,
  formatBobbinTime,
  localEntryDate,
  redactBobbinSecrets,
} from "./presentation";
import type {
  BobbinAppendIntent,
  BobbinDaily,
  BobbinVisibility,
} from "./types";

interface BobbinDailyViewProps {
  client: BobbinMobileClient;
  csrfToken: string;
}

export async function dailyAppendIdempotencyKey({
  entryDate,
  heading,
  body,
  visibility,
}: {
  entryDate: string;
  heading: string;
  body: string;
  visibility: BobbinVisibility;
}): Promise<string> {
  const canonical = JSON.stringify([
    "mobile-daily-v1",
    entryDate,
    heading.trim(),
    body,
    visibility,
  ]);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical));
  const hex = [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
  return `mobile-daily-v1:${hex}`;
}

function safeMessage(error: unknown): string {
  if (error instanceof BobbinMobileError) return error.message;
  if (error instanceof DOMException && error.name === "AbortError") return "";
  return "Bobbin could not complete that request.";
}

export function BobbinDailyView({ client, csrfToken }: BobbinDailyViewProps) {
  const [entryDate, setEntryDate] = useState(localEntryDate);
  const [daily, setDaily] = useState<BobbinDaily | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [heading, setHeading] = useState("");
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState<BobbinVisibility>("personal");
  const [submitting, setSubmitting] = useState(false);
  const [pendingIntent, setPendingIntent] = useState<BobbinAppendIntent | null>(null);
  const [notice, setNotice] = useState("");
  const requestRef = useRef<AbortController | null>(null);

  const beginRequest = useCallback(() => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    return controller;
  }, []);

  const loadDaily = useCallback(async () => {
    const controller = beginRequest();
    setLoading(true);
    setError("");
    try {
      const result = await client.callTool(
        "daily_get",
        { entry_date: entryDate },
        csrfToken,
        controller.signal,
      );
      setDaily(result);
    } catch (loadError) {
      if (loadError instanceof BobbinMobileError && loadError.code === "SPOOL_NOT_FOUND") {
        setDaily(null);
      } else {
        setError(safeMessage(loadError));
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [beginRequest, client, csrfToken, entryDate]);

  useEffect(() => {
    void loadDaily();
    return () => requestRef.current?.abort();
  }, [loadDaily]);

  const clearPendingIntent = () => {
    setPendingIntent(null);
    setNotice("");
  };

  const append = async () => {
    if (!heading.trim() || !body.trim()) return;
    setSubmitting(true);
    setError("");
    setNotice("");
    const controller = beginRequest();
    try {
      const intent = pendingIntent ?? {
        idempotency_key: await dailyAppendIdempotencyKey({
          entryDate,
          heading,
          body,
          visibility,
        }),
        entry_date: entryDate,
        heading: heading.trim(),
        body_md: body,
        visibility,
      };
      setPendingIntent(intent);
      const receipt = await client.appendDailyWithRetry(
        intent,
        csrfToken,
        controller.signal,
      );
      setPendingIntent(null);
      setHeading("");
      setBody("");
      setNotice(receipt.created ? "Section appended." : "Retry confirmed; section already exists.");
      setSubmitting(false);
      await loadDaily();
    } catch (appendError) {
      setError(safeMessage(appendError));
    } finally {
      if (!controller.signal.aborted) setSubmitting(false);
    }
  };

  return (
    <section className="bobbin-mobile__view" aria-labelledby="bobbin-daily-title">
      <header className="bobbin-mobile__view-header">
        <div>
          <span className="bobbin-mobile__eyebrow">Daily</span>
          <h1 id="bobbin-daily-title">Today</h1>
        </div>
        <label className="bobbin-mobile__date-control">
          <span>Date</span>
          <input
            aria-label="Daily date"
            type="date"
            value={entryDate}
            onChange={(event) => setEntryDate(event.target.value)}
          />
        </label>
      </header>

      {error && <div className="bobbin-mobile__alert" role="alert">{error}</div>}
      {notice && <div className="bobbin-mobile__notice" role="status">{notice}</div>}

      <div className="bobbin-mobile__section-list" aria-live="polite">
        {loading ? (
          <div className="bobbin-mobile__empty">Loading Daily…</div>
        ) : daily?.sections.length ? (
          daily.sections.map((section) => (
            <article className="bobbin-mobile__daily-section" key={section.id}>
              <header>
                <h2>{bobbinNodeTitle(section)}</h2>
                <span>{formatBobbinTime(section.updated_at ?? section.created_at)}</span>
              </header>
              <div className="bobbin-mobile__markdown-text">
                {section.body_md ? redactBobbinSecrets(section.body_md) : ""}
              </div>
            </article>
          ))
        ) : (
          <div className="bobbin-mobile__empty">No sections for this date yet.</div>
        )}
      </div>

      <form
        className="bobbin-mobile__composer"
        onSubmit={(event) => {
          event.preventDefault();
          void append();
        }}
      >
        <div className="bobbin-mobile__composer-heading">
          <div>
            <span className="bobbin-mobile__eyebrow">Append</span>
            <h2>New section</h2>
          </div>
          <label>
            <span>Visibility</span>
            <select
              aria-label="Section visibility"
              value={visibility}
              onChange={(event) => {
                setVisibility(event.target.value as BobbinVisibility);
                clearPendingIntent();
              }}
            >
              <option value="personal">Personal</option>
              <option value="work_private">Work private</option>
            </select>
          </label>
        </div>
        <label className="bobbin-mobile__field">
          <span>Heading</span>
          <input
            value={heading}
            maxLength={500}
            autoComplete="off"
            placeholder="What changed?"
            onChange={(event) => {
              setHeading(event.target.value);
              clearPendingIntent();
            }}
          />
        </label>
        <label className="bobbin-mobile__field">
          <span>Notes</span>
          <textarea
            value={body}
            rows={5}
            maxLength={1_000_000}
            autoComplete="off"
            placeholder="Write the durable update…"
            onChange={(event) => {
              setBody(event.target.value);
              clearPendingIntent();
            }}
          />
        </label>
        <button
          className="bobbin-mobile__primary-button"
          type="submit"
          disabled={submitting || !heading.trim() || !body.trim()}
        >
          {submitting ? "Appending…" : pendingIntent ? "Retry same append" : "Append section"}
        </button>
        {pendingIntent && !submitting && (
          <p className="bobbin-mobile__retry-note">
            Retry keeps the same in-memory request identity, so Bobbin will not duplicate the section.
          </p>
        )}
      </form>
    </section>
  );
}
